import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { listBackups, deleteBackup } from "./firestoreService";

// "20260603_103241" -> "2026-06-03 10:32"
const formatTimestamp = (ts) => {
  const m = /^(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})$/.exec(ts || "");
  if (!m) return ts || "—";
  return `${m[1]}-${m[2]}-${m[3]} ${m[4]}:${m[5]}`;
};

const formatBytes = (bytes) => {
  const n = Number(bytes || 0);
  if (n === 0) return "0 B";
  const units = ["B", "KiB", "MiB", "GiB", "TiB"];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  return `${(n / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

function Backups() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [data, setData] = useState({
    bucket: "",
    rows: [],
    count: 0,
    totalBytes: 0,
    newest: null,
    oldest: null,
    autoBackupCount: 0,
  });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await listBackups();
      setData(result);
    } catch (e) {
      setError(t("backups.errorGeneric", { message: e.message || "Unknown error" }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const onConfirmDelete = async () => {
    if (!deleteTarget) return;
    setActionBusy(true);
    setError("");
    setSuccess("");
    try {
      const res = await deleteBackup(deleteTarget.timestamp);
      setSuccess(
        t("backups.deleteSuccess", {
          timestamp: formatTimestamp(deleteTarget.timestamp),
          freed: formatBytes(res?.freedBytes),
        }),
      );
      setDeleteTarget(null);
      await load();
    } catch (e) {
      setError(t("backups.errorGeneric", { message: e.message || "Unknown error" }));
    } finally {
      setActionBusy(false);
    }
  };

  const componentChips = (has) => {
    const items = [
      { key: "firestore", label: t("backups.compFirestore") },
      { key: "storage", label: t("backups.compStorage") },
      { key: "auth", label: t("backups.compAuth") },
      { key: "manifest", label: t("backups.compManifest") },
    ];
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {items.map((it) => (
          <Chip
            key={it.key}
            size="small"
            variant={has?.[it.key] ? "filled" : "outlined"}
            color={has?.[it.key] ? "success" : "default"}
            label={it.label}
          />
        ))}
      </Stack>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h4" component="h1">
          {t("backups.title")}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={load}
          disabled={loading || actionBusy}
        >
          {t("backups.refresh")}
        </Button>
      </Stack>

      <Typography variant="body1" sx={{ mb: 1 }} color="text.secondary">
        {data.count === 0
          ? t("backups.empty")
          : t("backups.summary", {
              count: data.count,
              size: formatBytes(data.totalBytes),
            })}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
        {t("backups.context", {
          bucket: data.bucket || "—",
          autoCount: data.autoBackupCount || 0,
        })}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : data.rows.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">{t("backups.empty")}</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("backups.columnCreated")}</TableCell>
                <TableCell>{t("backups.columnTimestamp")}</TableCell>
                <TableCell>{t("backups.columnComponents")}</TableCell>
                <TableCell align="right">{t("backups.columnSize")}</TableCell>
                <TableCell align="right">{t("backups.columnActions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.rows.map((row) => {
                const isNewest = row.timestamp === data.newest;
                return (
                  <TableRow key={row.timestamp} hover>
                    <TableCell>{formatTimestamp(row.timestamp)}</TableCell>
                    <TableCell>
                      <code>{row.timestamp}</code>
                      {isNewest && (
                        <Chip
                          size="small"
                          color="primary"
                          variant="outlined"
                          label={t("backups.latestTag")}
                          sx={{ ml: 1 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>{componentChips(row.has)}</TableCell>
                    <TableCell align="right">{formatBytes(row.sizeBytes)}</TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={isNewest ? t("backups.latestProtected") : ""}
                        disableHoverListener={!isNewest}
                      >
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={actionBusy || isNewest}
                            onClick={() => setDeleteTarget(row)}
                          >
                            {t("backups.actionDelete")}
                          </Button>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>{t("backups.confirmDeleteTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("backups.confirmDeleteBody", {
              timestamp: deleteTarget ? formatTimestamp(deleteTarget.timestamp) : "",
              size: formatBytes(deleteTarget?.sizeBytes),
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={actionBusy}>
            {t("backups.confirmDeleteCancel")}
          </Button>
          <Button
            onClick={onConfirmDelete}
            color="error"
            variant="contained"
            disabled={actionBusy}
          >
            {actionBusy ? (
              <CircularProgress size={20} />
            ) : (
              t("backups.confirmDeleteConfirm")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Backups;
